import React from 'react';
import {
  Box,
  Flex,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Badge,
  Portal,
  Spinner,
  useColorModeValue,
} from '@chakra-ui/react';
import { LuChevronsUpDown, LuCheck, LuUserCog } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import OwnerAvatar from '../shared/OwnerAvatar';

/**
 * Workspace switcher dropdown (MI-268).
 *
 * Disambiguation rule (MI-345): a workspace is titled by whose it is, "Your
 * space" or "{owner}'s space", with its own name on the line below. The
 * bootstrap migration seeds every personal workspace as "Personal", so the
 * name alone tells no one's apart.
 *
 * Each workspace also shows whose it is as a picture: its owner's photo
 * (`owner_picture`, the owner's initial when there is none), which is your
 * own on your space and the host's on a shared one. Only the owner's, never
 * other members': the roster stays behind `member.read`.
 */
const WorkspaceSwitcher = ({ isCollapsed }) => {
  const { workspaces, current, currentId, loading, switchWorkspace, can } = useWorkspace();
  const { t } = useTranslation('workspaces');
  const navigate = useNavigate();
  const canManageMembers = can('member.read');
  const hoverBg = useColorModeValue('redesign.bg.hover', 'redesign.bg.hover');

  if (loading && !current) {
    return (
      <Flex align="center" justify="center" h="44px" px="8px">
        <Spinner size="xs" />
      </Flex>
    );
  }

  if (!current) {
    return null;
  }

  const ownerFirstName = (ws) => {
    if (ws.owner_full_name) return ws.owner_full_name.split(' ')[0];
    if (ws.owner_email) return ws.owner_email.split('@')[0];
    return '';
  };

  // Without an owner to name, a shared space keeps its own name as the title
  // and says your role below it.
  const titledByOwner = (ws) => ws.workspace_role === 'Owner' || Boolean(ownerFirstName(ws));

  const displayName = (ws) => {
    if (ws.workspace_role === 'Owner') return t('switcher.ownTitle');
    const owner = ownerFirstName(ws);
    return owner ? t('switcher.sharedTitle', { owner }) : ws.name;
  };

  const subtitle = (ws) => (
    titledByOwner(ws) ? ws.name : t(`roles.${ws.workspace_role}`, ws.workspace_role)
  );

  return (
    <Box px="8px" pt="10px" pb="6px">
      <Menu placement="bottom-start">
        <MenuButton
          as={Box}
          cursor="pointer"
          borderRadius="8px"
          border="1px solid"
          borderColor="redesign.border"
          bg="redesign.bg.raised"
          _hover={{ bg: hoverBg, borderColor: 'redesign.text.muted' }}
          px="10px"
          py="8px"
          transition="background 0.15s, border-color 0.15s"
          aria-label={t('switcher.ariaLabel')}
          // The guest tour's first stop: the space they were invited to.
          data-tour="tour-workspace"
        >
          {isCollapsed ? (
            <Flex align="center" justify="center">
              {/* Collapsed, the photo is all there is to say whose space is
                  active, so it is announced; elsewhere the name beside it
                  says so. */}
              <OwnerAvatar workspace={current} size={20} announce />
            </Flex>
          ) : (
            <Flex align="center" gap="8px">
              <OwnerAvatar workspace={current} size={24} />
              <Box flex={1} minW={0}>
                <Text
                  fontSize="md"
                  fontWeight="600"
                  color="redesign.heading"
                  noOfLines={1}
                >
                  {displayName(current)}
                </Text>
                <Text fontSize="xs" color="redesign.text.muted" noOfLines={1}>
                  {subtitle(current)}
                </Text>
              </Box>
              <Box color="redesign.text.muted" flexShrink={0}>
                <LuChevronsUpDown size={14} />
              </Box>
            </Flex>
          )}
        </MenuButton>
        <Portal>
          <MenuList
            fontSize="md"
            minW="280px"
            maxW="360px"
            zIndex={2000}
          >
            {workspaces.map((ws) => (
              <MenuItem
                key={ws.id}
                onClick={() => switchWorkspace(ws.id)}
                fontWeight={ws.id === currentId ? 600 : 400}
                icon={ws.id === currentId ? <LuCheck size={14} /> : <Box w="14px" />}
              >
                <Flex align="center" justify="space-between" w="100%" gap="6px">
                  <OwnerAvatar workspace={ws} size={24} mr="2px" />
                  <Box minW={0} flex={1}>
                    <Text noOfLines={1}>{displayName(ws)}</Text>
                    <Text fontSize="2xs" color="redesign.text.muted" noOfLines={1}>
                      {subtitle(ws)}
                    </Text>
                  </Box>
                  <Badge
                    fontSize="2xs"
                    colorScheme={ws.workspace_role === 'Owner' ? 'green' : 'gray'}
                    textTransform="none"
                    fontWeight="500"
                    flexShrink={0}
                  >
                    {t(`roles.${ws.workspace_role}`, ws.workspace_role)}
                  </Badge>
                </Flex>
              </MenuItem>
            ))}
            {canManageMembers && (
              <>
                <MenuDivider />
                <MenuItem
                  onClick={() => navigate('/members')}
                  icon={<LuUserCog size={14} />}
                  fontSize="sm"
                  color="redesign.text.muted"
                >
                  {t('switcher.manageMembers')}
                </MenuItem>
              </>
            )}
          </MenuList>
        </Portal>
      </Menu>
    </Box>
  );
};

export default WorkspaceSwitcher;
